<template>
  <div ref="root" class="pointer-events-none absolute-0">
    <ul v-for="(zones, bp) in dots" :key="bp" :class="visibilityClasses[bp]" role="list">
      <base-popover
        v-for="(zone, i) in zones"
        :key="i"
        v-slot="{ close, toggle, opened }"
        :close-on-leave="false"
        flip
        placement="top-start"
        :shift="popoverShift"
        strategy="fixed"
        tag="li"
        teleport="body"
        @close="onPopoverClose(zone)"
      >
        <base-button
          :aria-controls="opened ? `${zonesId}-${bp}-${i}` : undefined"
          :aria-expanded="opened || (mobileSheetOpen && activeZone === zone)"
          aria-haspopup="dialog"
          class="group pointer-events-auto absolute cursor-pointer b rounded-full transition-colors duration-500 -translate-x-1/2 -translate-y-1/2 hover:bg-white"
          :class="[sizeMap[zone.size].button, opened ? 'bg-white b-black z-4' : 'bg-grey-10 bg-op-50 b-white z-2']"
          :style="{
            left: `${zone.left}%`,
            top: `${zone.top}%`,
            ...(opened && buttonRaised && { zIndex: 900 }),
            ...(opened && buttonShiftX && { marginLeft: `-${buttonShiftX}px` }),
          }"
          @click="onToggleClick($event, toggle, opened, zone, close)"
        >
          <span class="sr-only">{{ zone.label }}</span>
          <div class="relative transition-transform duration-500" :style="[opened && { transform: 'rotate3d(0, 0, 1, 135deg)' }]">
            <div
              class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 group-hover:bg-grey-10"
              :class="[sizeMap[zone.size].line, opened ? 'bg-grey-10' : 'bg-white']"
              :style="{ height: '1px' }"
            />
            <div
              class="absolute left-1/2 top-1/2 rotate-90 -translate-x-1/2 -translate-y-1/2 group-hover:bg-grey-10"
              :class="[sizeMap[zone.size].line, opened ? 'bg-grey-10' : 'bg-white']"
              :style="{ height: '1px' }"
            />
          </div>
        </base-button>
        <base-popover-content
          :id="`${zonesId}-${bp}-${i}`"
          :aria-label="zone.linkedContent?.text || zone.label"
          class="pointer-events-auto z-overlay <md:max-w-full"
          :class="{ '<md:hidden': !zone.mobilePopOut }"
          role="dialog"
          tabindex="-1"
          @keydown.esc="closeCard(close)"
        >
          <cms-shared-hot-zones-product-card
            v-if="productCardTypes.includes(zone.linkedContent?.behavior)"
            :behavior="zone.linkedContent.behavior"
            :close
            compact
            :layout="zone.linkedContent.productCardTextPosition"
            :product-id="zone.linkedContent.productId"
            :url="zone.linkedContent.url"
            @open-quickshop="(productId) => openQuickshop({ productId, variant: 'detailed' })"
          />
          <div v-else class="f-col overflow-hidden rounded-sm bg-grey-10 bg-op-80 c-white shadow-lg backdrop-blur-md md:max-w-100">
            <base-link class="flex px-5 pb-6 pt-6" :target="zone.linkedContent?.targetAttribute" :to="zone.linkedContent?.url">
              <cms-rich-text
                v-if="zone.linkedContent?.htmlPopup"
                :content="{ richText: { html: zone.linkedContent?.htmlPopup } }"
              />
              <p v-else class="title-3">
                {{ zone.linkedContent?.text }}
              </p>
            </base-link>
          </div>
        </base-popover-content>
      </base-popover>
    </ul>
  </div>

  <base-sheet
    v-if="activeZone"
    v-model="mobileSheetOpen"
    :aria-label="activeZone.linkedContent?.text || activeZone.label"
    class="md:hidden"
    class-content="bg-op-90 backdrop-blur-md b b-white"
    :height-mode="isProductCardZone ? 'content' : 'snap'"
    :show-handle="false"
    :snap-points="[35]"
    @close="onSheetClose"
    @open="onSheetOpen"
  >
    <template #header>
      <base-button
        ref="sheetCloseButton"
        :aria-label="$t.close"
        class="ml-a px-4 py-3 md:hidden"
        @click="mobileSheetOpen = false"
      >
        <vf-icon name="close" size="md" />
      </base-button>
    </template>
    <cms-shared-hot-zones-product-card
      v-if="isProductCardZone"
      :key="activeZone.linkedContent.productId"
      :behavior="activeZone.linkedContent.behavior"
      :layout="activeZone.linkedContent.productCardTextPosition"
      :product-id="activeZone.linkedContent.productId"
      :url="activeZone.linkedContent.url"
      @open-quickshop="onSheetOpenQuickshop"
    />
    <div v-else class="f-col">
      <base-link
        class="flex px-5 pb-6"
        :target="activeZone.linkedContent?.targetAttribute"
        :to="activeZone.linkedContent?.url"
        @click="mobileSheetOpen = false"
      >
        <cms-rich-text
          v-if="activeZone.linkedContent?.htmlPopup"
          :content="{ richText: { html: activeZone.linkedContent?.htmlPopup } }"
        />
        <p v-else class="title-3">
          {{ activeZone.linkedContent?.text }}
        </p>
      </base-link>
    </div>
  </base-sheet>

  <dialog-quickshop />
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { HotZoneMedia, ImageMap } from '#types/media'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'

const props = defineProps<{
  media: Responsive<HotZoneMedia>
}>()

const { $gtm, $t, $viewport } = useNuxtApp()
const { ds: { breakpoints }, pages: { pdp }, components: { cms } } = useAppConfig()
const { quickshop: quickshopConfig } = pdp
const { closeCardOnQuickshop } = cms.hotZones
const productCardTypes = ['productcard', 'hotzonequickshop', 'oneclickhotzonequickshop']

const DialogQuickshop = createDialog('quickshop', quickshopConfig?.dialogOptions)

const PANEL_CLEARANCE = 416

const quickshopType = computed(() => getValueForBreakpoint($viewport.breakpoint, quickshopConfig?.dialogOptions?.type))
const quickshopPanelPosition = computed(() =>
  getValueForBreakpoint($viewport.breakpoint, quickshopConfig?.dialogOptions?.position ?? { sm: 'bottom', lg: 'right' }))

const sizeMap = {
  small: { button: 'h-6 w-6', line: 'w-2' },
  medium: { button: 'h-8 w-8', line: 'w-3' },
  large: { button: 'h-8 w-8 md:w-12 md:h-12', line: 'w-3 md:w-5' }
}

// Each media breakpoint keeps showing its zones until the next breakpoint that has its own media,
// so zones of two breakpoints are never visible (and focusable) at the same time
function generateVisibilityClasses(config: Record<string, unknown>, display: string = 'flex') {
  const definedBreakpoints = Object.keys(breakpoints).filter((bp) => bp in config)

  return Object.fromEntries(definedBreakpoints.map((bp, index) => {
    const next = definedBreakpoints[index + 1]
    const classes = bp === 'sm' ? display : `hidden ${bp}:${display}`

    return [bp, next ? `${classes} ${next}:hidden` : classes]
  }))
}

const zoneLabelKeys = {
  productcard: 'view',
  hotzonequickshop: 'preview',
  oneclickhotzonequickshop: 'quickShop'
} as const

const getZoneLabel = (entry: ImageMap) => {
  const ariaLabel = entry.ariaLabel?.trim()
  if (ariaLabel) return ariaLabel

  const behavior = entry.linkedContent?.behavior
  const key = behavior && zoneLabelKeys[behavior]
  const name = entry.linkedContent?.text?.trim()

  if (!key) return name ? `${$t.hotZoneLabel.details}: ${name}` : $t.hotZoneLabel.details

  return name ? replaceAll($t.hotZoneLabel[key], { name }) : $t.hotZoneViewProduct
}

const dots = computed(() => Object.fromEntries(
  Object.entries(props.media).map(([key, value]) => {
    if (value?.imageMap) {
      const mapped = value.imageMap.map((entry) => {
        const [xStr, yStr] = entry.center.split(',')
        const x = Number(xStr?.trim())
        const y = Number(yStr?.trim())

        const left = Number(((x / value.width) * 100).toFixed(3))
        const top = Number(((y / value.height) * 100).toFixed(3))

        return {
          left,
          top,
          label: getZoneLabel(entry),
          linkedContent: entry.linkedContent,
          mobilePopOut: entry.mobilePopOut,
          size: value.hotzoneIndicator ?? 'large',
        }
      })

      // Human natural sorting for the zones: top to bottom, then left to right
      return [key, mapped.toSorted((a, b) => a.top - b.top || a.left - b.left)]
    }

    return [key, []]
  })
))

const visibilityClasses = computed(() => generateVisibilityClasses(dots.value))
const root = useTemplateRef('root')

// The hotspots sit on the media, so its products are seen once the overlay is - only the zones of the
// active breakpoint are displayed (see `generateVisibilityClasses`)
whenever(useElementVisibility(root), () => {
  const products = (getValueForBreakpoint($viewport.breakpoint, dots.value) ?? [])
    .flatMap(({ linkedContent }) => linkedContent?.productId
      ? [{ id: linkedContent.productId, name: linkedContent.productName }]
      : [])

  if (products.length) $gtm.push('hotZone.onProductImpressions', products)
}, { once: true })

const zonesId = useId()
const mobileSheetOpen = ref(false)
const activeZone = shallowRef<any>(null)
const sheetCloseButton = useTemplateRef<ComponentPublicInstance>('sheetCloseButton')
const activeZoneButton = ref<HTMLElement | null>(null)
const buttonRaised = ref(false)
const buttonShiftX = ref(0)

let closeActivePopover: (() => void) | undefined
let openCardZone: unknown
// The hotspot that opened the current card, sheet or quickshop
let zoneTrigger: HTMLElement | undefined
let isDialogOpen = false

const getZoneCard = (trigger: HTMLElement) => document.getElementById(trigger.getAttribute('aria-controls') ?? '')

// The card is teleported out of the page flow so the hosting section can't crop it, which also leaves
// it at the very end of the focus order. As with the Expandable CTA, focus moves into the card instead
// and cycles between it and its hotspot until the card closes
const { activate, deactivate, pause, unpause } = useFocusTrap(
  () => activeZoneButton.value ? [activeZoneButton.value, getZoneCard(activeZoneButton.value)] : [],
  {
    initialFocus: () => {
      const card = activeZoneButton.value && getZoneCard(activeZoneButton.value)
      return card?.querySelector<HTMLElement>('a[href], button') || card || false
    },
    // Keeps the trap from throwing while nothing in it is tabbable (e.g. the card is still loading)
    fallbackFocus: () => {
      const trigger = activeZoneButton.value
      return (trigger && getZoneCard(trigger)) || trigger || document.body
    },
    // Unlike `allowOutsideClick`, lets focus land where the user clicked instead of pulling it back
    clickOutsideDeactivates: true,
    escapeDeactivates: false,
    returnFocusOnDeactivate: false
  }
)

const syncFocusTrap = () => {
  const trigger = activeZoneButton.value

  // A hotspot inside a dialog can't hand focus to a card teleported outside of that dialog
  if (!trigger || trigger.closest('[role="dialog"]')) return deactivate()
  // A dialog on top (e.g. quickshop) keeps focus until it closes
  if (isDialogOpen) return pause()

  activate()
  unpause()
}

// Deferred until the card has rendered and the trap has picked it up as a container
watch(activeZoneButton, () => nextTick(syncFocusTrap))

let removeDialogHooks: (() => void)[] = []

// Client only - the hooks are kept in module scope, which SSR requests share
onMounted(() => {
  removeDialogHooks = [
    // Synchronous, so the trap lets go before the dialog mounts and takes focus
    onDialogOpen(() => {
      isDialogOpen = true
      pause()
    }),
    onDialogClose(() => {
      isDialogOpen = false
      nextTick(syncFocusTrap)
    })
  ]
})

onBeforeUnmount(() => removeDialogHooks.forEach((remove) => remove()))

const router = useRouter()

const closeAll = () => {
  closeActivePopover?.()
  closeActivePopover = undefined
  mobileSheetOpen.value = false
}

const trackOpen = ({ linkedContent }: Pick<ImageMap, 'linkedContent'>) => {
  if (linkedContent?.productId)
    $gtm.push('hotZone.onProductClick', { id: linkedContent.productId, name: linkedContent.productName })
  else
    $gtm.push('hotZone.onClick', linkedContent?.delocalizedTracking || '')
}

// Product zones only track the click that opens them
const trackClose = ({ linkedContent }: Pick<ImageMap, 'linkedContent'>) => {
  if (linkedContent && !linkedContent.productId)
    $gtm.push('hotZone.onClose', linkedContent.delocalizedTracking || '')
}

// The base popover emits `close` again on its next update, which can land after another hotspot has
// already opened its card - only the open card's own close resets the state
const onPopoverClose = (zone: Pick<ImageMap, 'linkedContent'>) => {
  if (zone !== openCardZone) return

  trackClose(zone)
  openCardZone = undefined
  activeZoneButton.value = null
  deactivate()
}

// The card's content is destroyed on close, so Escape from inside it hands focus back to the hotspot
const closeCard = (close: () => void) => {
  const trigger = activeZoneButton.value
  close()
  trigger?.focus()
}

const removeRouteGuard = router.beforeEach(closeAll)
onBeforeUnmount(removeRouteGuard)

const isProductCardZone = computed(() =>
  productCardTypes.includes(activeZone.value?.linkedContent?.behavior),
)

const quickshopPanelOpen = ref(false)
let quickshopToken = 0

const openQuickshop = async (attrs: any) => {
  // Token guards against the previous dialog's close (awaited inside `open()`
  // when switching zones) clearing the flag for the newly opened panel
  const token = ++quickshopToken
  const trigger = zoneTrigger

  if (closeCardOnQuickshop || $viewport.breakpoint === 'sm') closeAll()

  quickshopPanelOpen.value = quickshopType.value === 'panel'

  try {
    // `closeDialogs()` resolves with a `closedReason` that the quickshop's own resolve type doesn't declare
    const { closedReason } = (await DialogQuickshop.open({
      ...attrs,
      gtmConfig: {
        ...attrs.gtmConfig,
        appSource: 'hot-zone',
        category: 'HotZone',
        list: 'HotZone'
      }
    }) ?? {}) as { closedReason?: string }
    // Escape closes the card too - let that settle before deciding where focus goes
    await nextTick()

    // Quickshop doesn't hand focus back when it closes. A card that is still open takes it back through
    // its focus trap, otherwise the hotspot does - unless another dialog or a navigation has taken over
    if (!activeZoneButton.value && closedReason !== 'dialog' && closedReason !== 'navigation')
      trigger?.focus({ preventScroll: true })
  }
  finally {
    if (token === quickshopToken) quickshopPanelOpen.value = false
  }
}

const popoverShift = computed(() => {
  if (quickshopPanelOpen.value && quickshopPanelPosition.value === 'right')
    return { padding: { right: PANEL_CLEARANCE } }

  return $viewport.breakpoint === 'sm' ? { padding: 16 } : undefined
})

watch([quickshopPanelOpen, activeZoneButton], ([panelOpen, btn]) => {
  if (!btn) {
    buttonRaised.value = false
    buttonShiftX.value = 0
  }
  else if (panelOpen) {
    buttonRaised.value = true

    if (quickshopPanelPosition.value === 'right')
      buttonShiftX.value = Math.max(0, btn.getBoundingClientRect().right - (window.innerWidth - PANEL_CLEARANCE))
  }
})

const onSheetOpenQuickshop = (productId: string) => {
  mobileSheetOpen.value = false
  openQuickshop({ productId })
}

const onSheetOpen = () => nextTick(() => sheetCloseButton.value?.$el.focus())

// Destroy the sheet (`v-if="activeZone"`) once it has finished sliding out.
// Keeping it mounted leaves the closed sheet visible in Safari
const onSheetClose = () => {
  if (activeZone.value) trackClose(activeZone.value)
  setTimeout(() => {
    if (!mobileSheetOpen.value) activeZone.value = null
  }, 300)
}

const onToggleClick = (
  event: Event,
  toggle: (e: Event) => void,
  opened: boolean,
  zone: ImageMap,
  close: () => void
) => {
  event.stopPropagation()
  if (!opened) trackOpen(zone)
  zoneTrigger = event.currentTarget as HTMLElement
  const behavior = zone.linkedContent?.behavior
  const isOneClick = behavior === 'oneclickhotzonequickshop'
  const isPanel = quickshopType.value === 'panel'

  if ($viewport.breakpoint === 'sm') {
    if (isOneClick) {
      openQuickshop({ productId: zone.linkedContent?.productId })
      return
    }

    if (!zone.mobilePopOut) {
      activeZone.value = zone
      // Open once the sheet is mounted, so its open handling (focus trap, scroll lock) runs
      nextTick(() => mobileSheetOpen.value = true)
      return
    }
  }

  // Skip the popover card when quickshop opens as a modal, or when the brand
  // closes the card on quickshop - either way the card would just be dismissed
  if (isOneClick && (!isPanel || closeCardOnQuickshop)) {
    openQuickshop({ productId: zone.linkedContent.productId, variant: 'detailed' })
    return
  }

  toggle(event)
  closeActivePopover = opened ? undefined : close
  openCardZone = opened ? undefined : zone
  activeZoneButton.value = opened ? null : event.currentTarget as HTMLElement

  if (isPanel) {
    if (isOneClick) {
      if (opened) {
        DialogQuickshop.close()
      }
      else {
        openQuickshop({ productId: zone.linkedContent?.productId, variant: 'detailed' })
      }
    }
  }
}
</script>
